import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ClaimTypes, NamedEntity } from "../../shared/CaseModel";
import { DialogShell } from "../general/DialogShell";

export interface ClaimEditorProps {
  title?: string;
  claimant?: NamedEntity;
  defendants?: NamedEntity[];
  setOff?: boolean;
  open: boolean;
  onClose: (claimTemplate?: {
    title: string;
    claimant: NamedEntity;
    defendants: NamedEntity[];
    type?: ClaimTypes;
  }) => void;
  parties?: NamedEntity[];
  type?: ClaimTypes;
}

function isClaimTitleValid(claimTitle?: string): boolean {
  return claimTitle !== undefined && claimTitle.trim().length > 0;
}
function isClaimantValid(claimant?: NamedEntity): boolean {
  return claimant !== undefined;
}
function isClaimTypeValid(claimType?: string): boolean {
  return claimType !== undefined && claimType.trim().length > 0;
}

export const ClaimEditorDialog = ({
  open,
  onClose,
  title: initialTitle = "",
  claimant: initialClaimant,
  defendants = [],
  parties = [],
  type: initialClaimType,
}: ClaimEditorProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.claims.editDialog",
  });
  const [title, setTitle] = useState(initialTitle);
  const [claimant, setClaimant] = useState(
    initialClaimant || (parties.length > 0 ? parties[0] : undefined),
  );
  const [defendantIds, setDefendantIds] = useState<string[]>(
    defendants.map((defendant) => defendant.id),
  );
  const [selectedClaimType, setSelectedClaimType] = useState<string>(
    initialClaimType ?? "",
  );
  const handleClaimTypesChange = (event: SelectChangeEvent<string>) => {
    setSelectedClaimType(event.target.value);
  };
  const areDefendantIdsValid = (defendantIds?: string[]): boolean =>
    defendantIds !== undefined &&
    !defendantIds.find((id) => id === claimant?.id);

  const canSubmit =
    isClaimTitleValid(title) &&
    isClaimantValid(claimant) &&
    areDefendantIdsValid(defendantIds) &&
    isClaimTypeValid(selectedClaimType);

  const handleDefendantsChange = (
    event: SelectChangeEvent<typeof defendantIds>,
  ) => {
    const {
      target: { value },
    } = event;
    setDefendantIds(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value,
    );
  };

  const close = () => onClose();
  const submit = () => {
    if (claimant === undefined) {
      onClose();
      return;
    }
    const defendants = parties.filter((party) =>
      defendantIds.includes(party.id),
    );
    onClose({
      title,
      claimant,
      defendants,
      type: selectedClaimType as ClaimTypes,
    });
  };

  // translate once, use twice
  const claimantLabel = t(
    parties.length === 0 ? "label.noParties" : "label.claimant",
  );
  const defendantsLabel = t(
    parties.length === 0 ? "label.noParties" : "label.defendants",
  );

  const sortedParties = [...parties].sort((p1, p2) =>
    p1.name.localeCompare(p2.name),
  );

  return (
    <DialogShell
      open={open}
      onClose={close}
      title={t("title")}
      description={t("description")}
      actions={
        <>
          <Button onClick={close} color="inherit">
            {t("cancel")}
          </Button>
          <Button
            onClick={submit}
            variant="contained"
            disabled={!canSubmit}
            autoFocus
          >
            {t("submit")}
          </Button>
        </>
      }
    >
      <TextField
        autoFocus
        size="small"
        id="title"
        label={t("tip.title")}
        type="text"
        fullWidth
        required
        variant="outlined"
        value={title}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setTitle(event.target.value);
        }}
        error={!isClaimTitleValid(title)}
        helperText={
          !isClaimTitleValid(title) ? t("helperText.missingTitle") : undefined
        }
      />
      <FormControl
        key={"claimant-selection"}
        fullWidth
        size="small"
        required
        error={!isClaimantValid(claimant)}
      >
        <InputLabel id="claimant-select-label">{claimantLabel}</InputLabel>
        <Select
          labelId="claimant-select-label"
          value={claimant?.id || ""}
          label={claimantLabel}
          onChange={(e: SelectChangeEvent) =>
            setClaimant(parties.find((party) => party.id === e.target.value))
          }
        >
          {sortedParties.map((party) => (
            <MenuItem key={party.id} value={party.id}>
              {party.name}
            </MenuItem>
          ))}
        </Select>
        {!isClaimantValid(claimant) && (
          <FormHelperText>{t("helperText.missingClaimant")}</FormHelperText>
        )}
      </FormControl>
      <FormControl
        key={"defendants-selection"}
        fullWidth
        size="small"
        error={!areDefendantIdsValid(defendantIds)}
      >
        <InputLabel id="defendants-select-label">{defendantsLabel}</InputLabel>
        <Select
          multiple
          value={defendantIds}
          label={defendantsLabel}
          onChange={handleDefendantsChange}
          labelId="defendants-select-label"
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {sortedParties
                .filter((p) => selected.includes(p.id))
                .map((p) => (
                  <Chip
                    key={p.id}
                    label={p.name}
                    size="small"
                    onDelete={() =>
                      setDefendantIds(selected.filter((id) => id !== p.id))
                    }
                    // a press on the chip would open the list of the select
                    // it sits in before the click on its button counted
                    onMouseDown={(event) => event.stopPropagation()}
                    sx={{ borderRadius: 1.25, fontWeight: 600 }}
                  />
                ))}
            </Box>
          )}
        >
          {sortedParties.map((party) => (
            <MenuItem key={party.id} value={party.id}>
              <Checkbox
                size="small"
                checked={defendantIds.includes(party.id)}
              />
              <ListItemText
                primary={party.name}
                secondary={
                  party.id === claimant?.id ? t("label.claimant") : undefined
                }
              />
            </MenuItem>
          ))}
        </Select>
        {!areDefendantIdsValid(defendantIds) && (
          <FormHelperText>{t("helperText.wrongDefendants")}</FormHelperText>
        )}
      </FormControl>
      <FormControl
        fullWidth
        size="small"
        required
        error={!isClaimTypeValid(selectedClaimType)}
      >
        <InputLabel id="claim-type-select-label">
          {t("label.claimType")}
        </InputLabel>
        <Select
          value={selectedClaimType}
          onChange={handleClaimTypesChange}
          label={t("label.claimType")}
          labelId="claim-type-select-label"
        >
          {Object.values(ClaimTypes)
            .map((type) => ({
              type,
              label: t(type, { keyPrefix: "case.claims.claimTypes" }),
            }))
            // in the order of their names, as the list shows them
            .sort((a, b) => a.label.localeCompare(b.label))
            .map(({ type, label }) => (
              <MenuItem key={type} value={type}>
                {label}
              </MenuItem>
            ))}
        </Select>
        {!isClaimTypeValid(selectedClaimType) && (
          <FormHelperText>{t("helperText.missingClaimType")}</FormHelperText>
        )}
      </FormControl>
    </DialogShell>
  );
};
